import React from 'react';
import { useSnackbar } from 'pounceV2';
import { extractErrorMessage } from 'Helpers/utils';
import { ApolloError } from '@apollo/client';
import {
  AIAuthorDetectionInputDetection,
  AIAuthorRunbookInputDetection,
  AIAuthorSkillInputSkill,
  AISummarizeKind,
  AISummarizeMetadata,
  LogStreamType,
  OutputLength,
} from 'Generated/schema';
import {
  getMutationHookForKind,
  extractAIInferenceStreamFromResponse,
  buildAISummarizeInput,
} from '../utils/aiKindUtils';
import type { AIMutationResponseData } from '../utils/aiKindUtils';
import {
  addAIInferenceStreamToCache,
  handleDrilldownCacheInvalidation,
} from '../utils/aiInferenceStreamCacheUtils';

interface AIOperationInput {
  alias: string;
  events?: string[];
  streamType?: LogStreamType;
  detection?: AIAuthorDetectionInputDetection;
  runbookDetection?: AIAuthorRunbookInputDetection;
  skill?: AIAuthorSkillInputSkill;
  currentProfile?: string;
  metadata?: AISummarizeMetadata;
  prompt?: string;
  outputLength?: OutputLength;
  parentStreamId?: string;
  private?: boolean;
  attachments?: string[];
}

interface UseAIMutationsProps {
  kind: AISummarizeKind;
  alias: string;
  onStreamCreated?: (streamId: string) => void;
  onError?: (error: string) => void;
}

interface UseAIMutationsReturn {
  runAISummarize: (input: AIOperationInput) => void;
  isLoading: boolean;
  error?: string;
}

/**
 * Unified hook for all AI mutation operations
 * Provides a consistent interface regardless of AI kind
 */
export const useAIMutationsV3 = ({
  kind,
  alias,
  onStreamCreated,
  onError,
}: UseAIMutationsProps): UseAIMutationsReturn => {
  const { pushSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  // Store the current prompt so it can be accessed in the update function
  const currentPromptRef = React.useRef<string | undefined>();

  const handleMutationError = React.useCallback(
    (err: ApolloError, context = 'AI operation') => {
      pushSnackbar({
        variant: 'error',
        title: `Error: ${context}`,
        description: extractErrorMessage(err) || `Failed to complete ${context}`,
      });
    },
    [pushSnackbar],
  );

  const useFetchHook = getMutationHookForKind(kind);

  const [runMutation] = useFetchHook({
    fetchPolicy: 'network-only',
    onError: err => {
      const errorMessage = `Failed to create AI ${kind.toLowerCase()}`;
      handleMutationError(err, errorMessage);
      setIsLoading(false);
      setError(errorMessage);
      onError?.(errorMessage);
    },
    onCompleted: (data: AIMutationResponseData) => {
      const newStreamId = extractAIInferenceStreamFromResponse(kind, data).streamId;
      setIsLoading(false);
      setError(undefined);
      onStreamCreated?.(newStreamId);
    },
    update: (cache, { data: responseData }) => {
      if (!alias || !responseData) {
        return;
      }

      const newAIInferenceStream = extractAIInferenceStreamFromResponse(kind, responseData);

      // Add to cache
      addAIInferenceStreamToCache(cache, alias, {
        ...newAIInferenceStream,
        alias,
        userPrompt: currentPromptRef.current || null,
        usage: null, // usage is not in the mutation selection set; populated later via stream polling
      });

      // Handle drilldown cache invalidation if needed
      if (newAIInferenceStream.parentStreamId) {
        handleDrilldownCacheInvalidation(
          cache,
          newAIInferenceStream.parentStreamId,
          newAIInferenceStream.streamId,
        );
      }
    },
  });

  const runAISummarize = React.useCallback(
    (input: AIOperationInput) => {
      setIsLoading(true);
      setError(undefined);
      // Store the prompt for use in cache update
      currentPromptRef.current = input.prompt;

      // Build base input
      const baseInput = {
        prompt: input.prompt,
        outputLength: input.outputLength,
        metadata: input.metadata,
        private: input.private,
        attachments: input.attachments,
      };

      // Build kind-specific input
      const mutationInput = buildAISummarizeInput(
        kind,
        alias,
        baseInput,
        input.events,
        input.streamType,
        input.detection,
        input.skill,
        input.currentProfile,
        input.runbookDetection,
      );

      // Handle drilldown scenarios - only makes sense if we have a prompt
      if (input.parentStreamId && input.prompt) {
        mutationInput.metadata = {
          ...(mutationInput.metadata || {}),
          kind: AISummarizeKind.DrillDown,
          parentStreamId: input.parentStreamId,
        };
      }

      runMutation({
        variables: {
          input: mutationInput,
        },
      });
    },
    [kind, alias, runMutation],
  );

  return {
    runAISummarize,
    isLoading,
    error,
  };
};
